import React, { useMemo, useState } from 'react';
import {
    Search,
    ArrowLeft,
    Users,
    Wallet,
    UserCheck,
    Download,
    Filter,
    Copy,
    CheckCircle,
    DollarSign,
    Building2
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api, { bondsAPI, formatCurrency, formatDate } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { createResponseSelector } from '../../utils/apiHelpers';

const BondHolders = () => {
    const navigate = useNavigate();
    const { bondId } = useParams();
    const [searchTerm, setSearchTerm] = useState('');
    const [holderTypeFilter, setHolderTypeFilter] = useState('all');
    const [copiedAddress, setCopiedAddress] = useState(null);

    // Fetch bond data
    const { data: bondData, isPending: bondLoading } = useQuery({
        queryKey: ['bond', bondId],
        queryFn: () => bondsAPI.getBond(bondId),
        select: createResponseSelector('bond', {})
    });

    const bond = bondData || {};

    // Fetch real token holders via backend
    const { data: holdersData, isPending: holdersLoading } = useQuery({
        queryKey: ['bondHolders', bondId],
        enabled: !!bondId,
        queryFn: async () => {
            const resp = await api.get(`/api/bonds/${bondId}/holders`);
            return resp.data?.data;
        }
    });

    const holders = useMemo(() => {
        if (!holdersData) return [];
        const token = holdersData.token || holdersData;
        const holdersArr = token.holders || holdersData.holders || [];
        const totalSupplyNum = parseFloat(token.totalSupply ?? holdersData.totalSupply ?? '0') || 0;

        const list = holdersArr.map((h, idx) => {
            const amount = parseFloat(h.balance ?? '0') || 0;
            let pct = 0;
            if (typeof h.percentage === 'string') {
                pct = parseFloat(h.percentage.replace('%', '')) || 0;
            } else if (typeof h.percentage === 'number') {
                pct = h.percentage;
            } else if (totalSupplyNum > 0) {
                pct = (amount / totalSupplyNum) * 100;
            }
            return {
                investorName: h.primaryAccountHolderName || '-',
                walletAddress: h.address,
                holderType: 'Retail',
                holdingAmount: amount,
                holdingPercentage: pct.toFixed(2),
                firstPurchaseDate: null,
                lastTransactionDate: null,
                status: 'Active',
                kycStatus: 'Verified'
            };
        });

        return list.sort((a, b) => b.holdingAmount - a.holdingAmount);
    }, [holdersData]);

    // Derive unique wallet addresses for account type enrichment
    const holderAddresses = useMemo(() => {
        return Array.from(new Set(holders.map(h => (h.walletAddress || '').toLowerCase()).filter(Boolean)));
    }, [holders]);

    // Fetch bank account types for each wallet address (if wallet exists in system)
    const { data: walletAccountTypes = {} } = useQuery({
        queryKey: ['wallet-account-types-batch', bondId, holderAddresses],
        enabled: holderAddresses.length > 0,
        queryFn: async () => {
            const resp = await api.post('/api/wallets/address/batch', { addresses: holderAddresses });
            const wallets = resp?.data?.wallets || resp?.data?.data?.wallets || [];
            const map = {};
            wallets.forEach(w => { if (w.walletAddress && w.bankAccountDetails?.accountType) { map[w.walletAddress.toLowerCase()] = w.bankAccountDetails.accountType; } });
            return map;
        }
    });

    // Helper to derive account type from multiple potential shapes
    const getAccountType = (holder) => {
        const addrLc = (holder.walletAddress || '').toLowerCase();
        return (
            walletAccountTypes[addrLc] ||
            holder?.bankAccountDetails?.accountType ||
            holder?.bankaccountdetails?.accountType ||
            holder?.accountType ||
            null
        );
    };

    const copyToClipboard = async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedAddress(id);
            setTimeout(() => setCopiedAddress(null), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    // Filter holders
    const filteredHolders = holders.filter(holder => {
        const matchesSearch =
            holder.investorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            holder.walletAddress.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesType = holderTypeFilter === 'all' || holder.holderType === holderTypeFilter;

        return matchesSearch && matchesType;
    });

    // Calculate summary stats
    const totalHolders = holders.length;
    const institutionalHolders = holders.filter(h => h.holderType === 'Institutional').length;
    const retailHolders = holders.filter(h => h.holderType === 'Retail').length;
    const totalHolding = holders.reduce((sum, holder) => sum + holder.holdingAmount, 0);
    const averageHolding = totalHolders > 0 ? totalHolding / totalHolders : 0;

    const getStatusBadge = (status) => {
        const statusClasses = {
            'Active': 'bg-green-100 text-green-800',
            'Inactive': 'bg-red-100 text-red-800',
            'Verified': 'bg-blue-100 text-blue-800',
            'Pending': 'bg-yellow-100 text-yellow-800'
        };

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
                {status}
            </span>
        );
    };

    const getHolderTypeBadge = (type) => {
        const typeClasses = {
            'Institutional': 'bg-purple-100 text-purple-800',
            'Retail': 'bg-blue-100 text-blue-800'
        };

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${typeClasses[type] || 'bg-gray-100 text-gray-800'}`}>
                {type}
            </span>
        );
    };

    if (bondLoading || holdersLoading) {
        return <LoadingSpinner size="lg" text="Loading bond holders..." />;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-6">
                        <div className="flex items-center">
                            <button
                                onClick={() => navigate('/issuer/token-holdings')}
                                className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Bond Holders</h1>
                                <p className="mt-2 text-sm text-gray-600">
                                    {bond.bondName} - Token holder details and distribution
                                </p>
                            </div>
                        </div>
                        <button className="btn-primary flex items-center space-x-2">
                            <Download className="w-4 h-4" />
                            <span>Export Holders</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

            {/* Bond Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                <div className="card">
                    <div className="card-body">
                        <div className="flex items-center">
                            <div className="flex-shrink-0 p-3 rounded-lg bg-primary-100">
                                <Users className="w-6 h-6 text-primary-600" />
                            </div>
                            <div className="ml-4 min-w-0 flex-1">
                                <p className="text-sm font-medium text-secondary-600 truncate">
                                    Total Holders
                                </p>
                                <p className="text-2xl font-bold">
                                    {totalHolders}
                                </p>
                                <p className="text-xs text-primary-600 mt-1">
                                    Total number of bond holders
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <div className="flex items-center">
                            <div className="flex-shrink-0 p-3 rounded-lg bg-info-100">
                                <Building2 className="w-6 h-6 text-info-600" />
                            </div>
                            <div className="ml-4 min-w-0 flex-1">
                                <p className="text-sm font-medium text-secondary-600 truncate">
                                    Institutional
                                </p>
                                <p className="text-2xl font-bold">
                                    {institutionalHolders}
                                </p>
                                <p className="text-xs text-info-600 mt-1">
                                    Institutional investors
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <div className="flex items-center">
                            <div className="flex-shrink-0 p-3 rounded-lg bg-warning-100">
                                <UserCheck className="w-6 h-6 text-warning-600" />
                            </div>
                            <div className="ml-4 min-w-0 flex-1">
                                <p className="text-sm font-medium text-secondary-600 truncate">
                                    Retail
                                </p>
                                <p className="text-2xl font-bold">
                                    {retailHolders}
                                </p>
                                <p className="text-xs text-warning-600 mt-1">
                                    Retail investors
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <div className="flex items-center">
                            <div className="flex-shrink-0 p-3 rounded-lg bg-success-100">
                                <DollarSign className="w-6 h-6 text-success-600" />
                            </div>
                            <div className="ml-4 min-w-0 flex-1">
                                <p className="text-sm font-medium text-secondary-600 truncate">
                                    Avg. Holding
                                </p>
                                <p className="text-2xl font-bold">
                                    {formatCurrency(averageHolding)}
                                </p>
                                <p className="text-xs text-success-600 mt-1">
                                    Average holding value
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters and Search */}
            <div className="card">
                <div className="card-body">
                    <div className="flex flex-col sm:flex-row gap-4">
                        {/* Search */}
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Search by name or wallet address..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="form-input pl-10"
                                />
                            </div>
                        </div>

                        {/* Holder Type Filter */}
                        <div className="w-full sm:w-48">
                            <select
                                value={holderTypeFilter}
                                onChange={(e) => setHolderTypeFilter(e.target.value)}
                                className="form-input"
                            >
                                <option value="all">All Types</option>
                                <option value="Institutional">Institutional</option>
                                <option value="Retail">Retail</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Token Holders Table */}
            <div className="card">
                <div className="card-header">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-medium text-secondary-900">
                            Token Holders ({filteredHolders.length})
                        </h2>
                        <div className="flex items-center space-x-2">
                            <span className="text-sm text-secondary-600">
                                {searchTerm && `Filtered by "${searchTerm}"`}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="table">
                        <thead className="table-header">
                            <tr>
                                <th className="table-header-cell">Primary Account Holder</th>
                                <th className="table-header-cell">Wallet Address</th>
                                <th className="table-header-cell">Holding Amount</th>
                                <th className="table-header-cell">Percentage</th>
                                <th className="table-header-cell">Verification Status</th>
                                <th className="table-header-cell">Status</th>
                                <th className="table-header-cell">Last Transaction</th>
                            </tr>
                        </thead>
                        <tbody className="table-body">
                            {filteredHolders.length > 0 ? (
                                filteredHolders.map((holder, idx) => (
                                    <tr key={holder.walletAddress || idx} className="table-row">
                                        <td className="table-cell">
                                            <div className="font-medium text-secondary-900">{holder.investorName}</div>
                                        </td>
                                        <td className="table-cell">
                                            <div className="flex items-center space-x-2">
                                                <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                                                    {holder.walletAddress?.slice(0, 8)}...{holder.walletAddress?.slice(-6)}
                                                </code>
                                                {(() => {
                                                    const acctType = getAccountType(holder); return acctType ? (
                                                        <span className="text-[10px] tracking-wide uppercase bg-primary-50 text-primary-700 px-2 py-0.5 rounded border border-primary-100" title={`Account Type: ${acctType}`}>
                                                            {acctType}
                                                        </span>
                                                    ) : null;
                                                })()}
                                                <button
                                                    onClick={() => copyToClipboard(holder.walletAddress, holder.walletAddress)}
                                                    className="text-secondary-400 hover:text-secondary-600"
                                                    title="Copy address"
                                                >
                                                    {copiedAddress === holder.walletAddress ? (
                                                        <CheckCircle className="w-4 h-4 text-success-600" />
                                                    ) : (
                                                        <Copy className="w-4 h-4" />
                                                    )}
                                                </button>
                                            </div>
                                        </td>
                                        <td className="table-cell">
                                            <div className="font-medium text-secondary-900">
                                                {formatCurrency(holder.holdingAmount)}
                                            </div>
                                        </td>
                                        <td className="table-cell">
                                            <div className="font-medium text-secondary-900">
                                                {holder.holdingPercentage}%
                                            </div>
                                        </td>
                                        <td className="table-cell">
                                            {getStatusBadge(holder.kycStatus)}
                                        </td>
                                        <td className="table-cell">
                                            {getStatusBadge(holder.status)}
                                        </td>
                                        <td className="table-cell">
                                            <div className="text-sm text-secondary-900">
                                                {holder.lastTransactionDate ? formatDate(holder.lastTransactionDate) : '-'}
                                            </div>
                                            <div className="text-xs text-secondary-500">
                                                First: {holder.firstPurchaseDate ? formatDate(holder.firstPurchaseDate) : '-'}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="table-cell text-center py-12">
                                        <div className="text-secondary-500">
                                            <Users className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
                                            <p className="text-lg font-medium mb-2">
                                                {searchTerm ? 'No holders found' : 'No token holders yet'}
                                            </p>
                                            <p className="text-sm mb-4">
                                                {searchTerm
                                                    ? `No holders match your search "${searchTerm}"`
                                                    : 'This bond has no token holders yet'
                                                }
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Top Holders Distribution */}
            {filteredHolders.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Top 10 Holders */}
                    <div className="card">
                        <div className="card-header">
                            <h3 className="text-lg font-medium text-secondary-900">Top 10 Holders</h3>
                        </div>
                        <div className="card-body">
                            <div className="space-y-3">
                                {holders
                                    .slice(0, 10)
                                    .map((holder, index) => (
                                        <div key={holder.walletAddress || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center space-x-3">
                                                <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-medium">
                                                    {index + 1}
                                                </div>
                                                <div>
                                                    <div className="font-medium text-secondary-900">{holder.walletAddress?.slice(0, 8)}...{holder.walletAddress?.slice(-6)}</div>
                                                    <div className="text-sm text-secondary-500">{holder.investorName}</div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-medium text-secondary-900">
                                                    {formatCurrency(holder.holdingAmount)}
                                                </div>
                                                <div className="text-sm text-secondary-500">
                                                    {holder.holdingPercentage}%
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>

                    {/* Holder Type Distribution */}
                    <div className="card">
                        <div className="card-header">
                            <h3 className="text-lg font-medium text-secondary-900">Distribution by Type</h3>
                        </div>
                        <div className="card-body">
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                            <div className="w-4 h-4 bg-purple-500 rounded"></div>
                                            <span className="text-sm font-medium text-secondary-900">Institutional</span>
                                        </div>
                                        <span className="text-sm text-secondary-600">
                                            {institutionalHolders} holders ({((institutionalHolders / totalHolders) * 100).toFixed(1)}%)
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-purple-500 h-2 rounded-full"
                                            style={{ width: `${(institutionalHolders / totalHolders) * 100}%` }}
                                        ></div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                            <div className="w-4 h-4 bg-blue-500 rounded"></div>
                                            <span className="text-sm font-medium text-secondary-900">Retail</span>
                                        </div>
                                        <span className="text-sm text-secondary-600">
                                            {retailHolders} holders ({((retailHolders / totalHolders) * 100).toFixed(1)}%)
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-blue-500 h-2 rounded-full"
                                            style={{ width: `${(retailHolders / totalHolders) * 100}%` }}
                                        ></div>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-gray-200">
                                    <div className="text-sm text-secondary-600">
                                        Total Value: {formatCurrency(totalHolding)}
                                    </div>
                                    <div className="text-sm text-secondary-600">
                                        Average Holding: {formatCurrency(averageHolding)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
};

export default BondHolders;
